import { all, call, put, select, takeLatest } from "redux-saga/effects"
import { withCallback } from "redux-saga-callback"

import apiClient from "@api/client"
import { IUser } from "@api/schema"
import { IUserWriteDTO, transformEntityToWriteDTO } from "@api/schema-dto"
import { Routes } from "@basics/routes"
import {
  IRegisterUserAction,
  RegistrationUsecases,
} from "@redux/actions/registration"
import { UNKNOWN_REQUEST_ERROR } from "@redux/common/constants"
import { EntityType, ScopeTypes } from "@redux/common/reduxTypes"
import { showErrorsInTestEnvironment } from "@redux/common/sagaErrorHelpers"
import { createModelSuccessAction } from "@redux/common/scopedObject/actions"
import { usecaseRequestRunningAction, usecaseRequestSuccessAction } from "@redux/common/scopedRequest/actions"
import { IOnboardingState } from "@redux/usecases/onboarding/reducers"
import { selectOnboardingData } from "@redux/usecases/onboarding/selectors"
import { SubmissionError } from "@services/submissionError"
import { BASE_URL } from "config"

export function* registrationWatcherSaga(): any {
  yield all([
    takeLatest(RegistrationUsecases.RegisterUser, withCallback(registerUserSaga)),
  ])
}

/**
 * Saga to be called when a user registers on the platform.
 */
function* registerUserSaga(action: IRegisterUserAction) {
  const { onSuccess, setErrors, setSubmitting } = action.actions

  try {
    yield put(usecaseRequestRunningAction(ScopeTypes.UserOperation))
    // the API uses {{param}} as placeholder while Next uses [param]:
    const route = Routes.ConfirmAccount.replace(/\[/g, "{{").replace(/\]/g, "}}")

    // transform entity to its DTO representation, if applicable
    const userWriteDTO: IUserWriteDTO = transformEntityToWriteDTO(EntityType.User, action.user)

    // Attach onboarding data for meanwhile-storage by the backend
    // to be processed after user verification was finished.
    // Done here and not in transformEntityToWriteDTO because access to the state is needed
    const onboardingData: IOnboardingState = yield select(selectOnboardingData)
    userWriteDTO.metadata = [
      onboardingData
    ]

    userWriteDTO.verificationUrl = BASE_URL + route

    const newUser: IUser = yield call(apiClient.registerUser, userWriteDTO)

    yield put(createModelSuccessAction(EntityType.User, newUser))
    yield put(usecaseRequestSuccessAction(ScopeTypes.UserOperation, newUser))

    if (onSuccess) {
      yield call(onSuccess)
    }

    return newUser
  } catch (err) {
    if (err instanceof Error) {
      if (err instanceof SubmissionError) {
        yield call(setErrors, err.errors)
      } else {
        yield call(setErrors, { error: err.message })
      }
    }

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR
    showErrorsInTestEnvironment("registerUserSaga", errorMessage, action, err)
    yield put(usecaseRequestRunningAction(ScopeTypes.UserOperation, errorMessage))
    if (setSubmitting) {
      yield call(setSubmitting, false)
    }

    return null
  }
}
